import { readString } from './snapshotReaders';
import type { Snapshot } from '../types/agreements.types';
import type { Customer } from '@/features/customers/types/customers.types';

export type HolderFieldKey =
    | 'name'
    | 'businessName'
    | 'vatNumber'
    | 'email'
    | 'phone'
    | 'mobilePhone'
    | 'address';

export interface ResolvedHolderField {
    key: HolderFieldKey;
    label: string;
    current: string | undefined;
    asSold: string | undefined;
    /** True only when both values are present and differ. */
    changed: boolean;
    /** Set for the address, which renders stacked rather than as one line. */
    currentLines?: string[];
    asSoldLines?: string[];
}

export interface ResolvedHolder {
    fields: ResolvedHolderField[];
    hasAnyChange: boolean;
}

const join = (parts: Array<string | undefined>): string | undefined => {
    const present = parts.filter(Boolean);

    return present.length > 0 ? present.join(' ') : undefined;
};

const addressLines = (parts: Array<string | undefined>): string[] =>
    parts.filter((part): part is string => Boolean(part));

/** Live values win field by field; the snapshot fills any gap. A malformed or
 *  absent snapshot yields the empty state rather than throwing. */
export function resolveHolder(snapshot: Snapshot | undefined, live: Customer | undefined): ResolvedHolder {
    const snap = snapshot ?? {};

    const soldAddress = addressLines([
        readString(snap.address_line_1),
        readString(snap.address_line_2),
        readString(snap.city),
        readString(snap.postcode),
    ]);
    const liveAddress = live
        ? addressLines([live.address_line_1, live.address_line_2, live.city, live.postcode])
        : [];

    const fields: ResolvedHolderField[] = [
        toField(
            'name',
            'Name',
            live ? join([live.title, live.forename, live.surname]) : undefined,
            join([readString(snap.title), readString(snap.forename), readString(snap.surname)]),
        ),
        toField('businessName', 'Business name', readLive(live?.business_name), readString(snap.business_name)),
        toField('vatNumber', 'VAT number', readLive(live?.vat_number), readString(snap.vat_number)),
        toField('email', 'Email', readLive(live?.email), readString(snap.email)),
        toField('phone', 'Phone', readLive(live?.phone), readString(snap.phone)),
        toField('mobilePhone', 'Mobile', readLive(live?.mobile_phone), readString(snap.mobile_phone)),
        toAddressField(liveAddress, soldAddress),
    ];

    return { fields, hasAnyChange: fields.some((field) => field.changed) };
}

const readLive = (value: string | undefined): string | undefined => readString(value);

function toField(
    key: HolderFieldKey,
    label: string,
    live: string | undefined,
    asSold: string | undefined,
): ResolvedHolderField {
    const current = live ?? asSold;

    return {
        key,
        label,
        current,
        asSold,
        changed: current !== undefined && asSold !== undefined && current !== asSold,
    };
}

/** Compared as one value so a moved house reads as a single change, but carried
 *  as lines so the panel can stack it the way an address is written. */
function toAddressField(liveLines: string[], soldLines: string[]): ResolvedHolderField {
    const field = toField(
        'address',
        'Address',
        liveLines.length > 0 ? liveLines.join(', ') : undefined,
        soldLines.length > 0 ? soldLines.join(', ') : undefined,
    );

    return {
        ...field,
        currentLines: liveLines.length > 0 ? liveLines : soldLines,
        asSoldLines: soldLines,
    };
}
